
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useBrandManager } from "@/hooks/useBrandManager";

interface SalesChannel {
  id: string;
  name: string;
  description: string;
  brand_id: string;
  created_at: string;
  updated_at: string;
}

interface CreateSalesChannelData {
  name: string;
  description: string;
}

interface UpdateSalesChannelData {
  name: string;
  description: string;
}

export const useSalesChannels = () => {
  const [salesChannels, setSalesChannels] = useState<SalesChannel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();

  const fetchSalesChannels = async () => {
    if (!user) {
      setSalesChannels([]);
      return;
    }

    if (!currentBrandId) {
      setSalesChannels([]);
      return;
    }

    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('sales_channels')
        .select('*')
        .eq('brand_id', currentBrandId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[useSalesChannels] Supabase error:', error);
        throw error;
      }
      
      setSalesChannels(data || []);
    } catch (error: any) {
      console.error('[useSalesChannels] Error fetching sales channels:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch sales channels",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createSalesChannel = async (data: CreateSalesChannelData): Promise<boolean> => {
    if (!user || !currentBrandId) {
      toast({
        title: "Error",
        description: "Please select a brand first",
        variant: "destructive",
      });
      return false;
    }

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('sales_channels')
        .insert([{
          name: data.name,
          description: data.description,
          brand_id: currentBrandId,
        }]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Sales channel created successfully",
      });

      await fetchSalesChannels();
      return true;
    } catch (error: any) {
      console.error('Error creating sales channel:', error);
      toast({
        title: "Error",
        description: "Failed to create sales channel",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateSalesChannel = async (id: string, data: UpdateSalesChannelData): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('sales_channels')
        .update({
          name: data.name,
          description: data.description,
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Sales channel updated successfully",
      });

      await fetchSalesChannels();
      return true;
    } catch (error: any) {
      console.error('Error updating sales channel:', error);
      toast({
        title: "Error",
        description: "Failed to update sales channel",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSalesChannel = async (id: string): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('sales_channels')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Sales channel deleted successfully",
      });

      await fetchSalesChannels();
      return true;
    } catch (error: any) {
      console.error('Error deleting sales channel:', error);
      toast({
        title: "Error",
        description: "Failed to delete sales channel",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesChannels();
  }, [user, currentBrandId]);

  // Listen for brand changes
  useEffect(() => {
    const handleBrandChanged = () => {
      fetchSalesChannels();
    };

    window.addEventListener('brandChanged', handleBrandChanged);
    return () => {
      window.removeEventListener('brandChanged', handleBrandChanged);
    };
  }, []);

  return {
    salesChannels,
    isLoading,
    createSalesChannel,
    updateSalesChannel,
    deleteSalesChannel,
    refetch: fetchSalesChannels,
  };
};
